import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useNavigate } from 'react-router-dom';
import { APP_URLS } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import emptyDocIcon from '../../assets/empty_doc.svg';
import AddIcon from '@mui/icons-material/Add';
import EmployeeList from './EmployeeList';
import EmployeeForm from './EmployeeForm';
import './ManageEmployee.css';

const Employees = () => {
  const { mode } = useThemeMode();
  const { hasAddPermission } = usePermissions();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch employees when component mounts
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await dataAPI.getAllEmployees();
        setEmployees(response.data || []);
        setInfoMessage(response.message || response.data?.message || '');
      } catch (error) {
        console.error('Error fetching employees:', error);
        setError(error.message || 'Failed to fetch employees');
        setEmployees([]);
        setInfoMessage('');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const handleDeleteEmployee = async (id) => {
    try {
      await dataAPI.deleteEmployee(id);
      setEmployees(prev => prev.filter(emp => emp.id !== id));
    } catch (error) {
      console.error('Error deleting employee:', error);
    }
  };

  const handleEditEmployee = (employee) => {
    setEditData(employee);
    setIsFormOpen(true);
  };

  const handleViewEmployee = (employee) => {
    if (employee?.id) {
      navigate(APP_URLS.EMPLOYEE_DETAILS.replace(':employeeId', employee.id), {
        state: { employee }
      });
    }
  };

  return (
    <div
      className="employees-page"
      style={{
        padding: '20px',
        width: '100%',
        height: '100%',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff'
      }}
    >
      {/* Loading State */}
      {isLoading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)'
        }}>
          <div style={{
            fontSize: '16px',
            color: mode === 'dark' ? '#FFFFFF' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Loading employees...
          </div>
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '16px',
            color: '#f44336',
            marginBottom: '16px',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {error}
          </div>
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: '#26C3C0',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Content State */}
      {!isLoading && !error && employees.length === 0 ? (
        // Empty State
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          textAlign: 'center'
        }}>
          {/* Info Banner */}
          {infoMessage ? (
            <div style={{
              width: '100%',
              maxWidth: '800px',
              backgroundColor: 'rgba(38, 195, 192, 0.08)',
              border: '1px solid #26C3C0',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(38, 195, 192, 0.2)'
            }}>
              <span style={{
                color: mode === 'dark' ? '#FFFFFF' : '#333333',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
                lineHeight: '1.4'
              }}>
                {infoMessage}
              </span>
            </div>
          ) : null}
          {/* Empty Employee Icon */}
          <div style={{
            marginBottom: '24px'
          }}>
            <img 
              src={emptyDocIcon} 
              alt="No Employees" 
              style={{
                width: '71px',
                height: '59px',
                filter: mode === 'dark' ? 'brightness(1)' : 'brightness(0.8)'
              }}
            />
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: '0 0 16px 0',
            fontFamily: 'Urbanist, Arial, sans-serif',
            lineHeight: '1.2'
          }}>
            No Employees Yet
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '16px',
            color: mode === 'dark' ? '#FFFFFF' : '#333333',
            margin: '0 0 32px 0',
            maxWidth: '600px',
            lineHeight: '1.5',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Start building your team by adding employees to your company. Create, organize, and manage user profiles to assign roles, track activity, and collaborate efficiently.
          </p>

          {/* Add Employee Button */}
          {hasAddPermission('employee') && (
            <button 
              onClick={() => setIsFormOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#26C3C0',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#22B0AD';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#26C3C0';
              }}
            >
              <AddIcon style={{ fontSize: '20px' }} />
              Add Employee
            </button>
          )}
        </div>
      ) : (!isLoading && !error && employees.length > 0) ? (
        // Employee List View
        <div>
          {/* Info Banner */}
          {infoMessage ? (
            <div style={{
              backgroundColor: 'rgba(38, 195, 192, 0.08)',
              border: '1px solid #26C3C0',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              boxShadow: '0 2px 8px rgba(38, 195, 192, 0.2)'
            }}>
              <span style={{
                color: mode === 'dark' ? '#FFFFFF' : '#333333',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
                lineHeight: '1.4'
              }}>
                {infoMessage}
              </span>
            </div>
          ) : null}
          {/* Search and Add Buttons */}
          <div
            className="employees-header"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              gap: '16px'
            }}
          >
            {/* Search Input */}
            <div style={{
              flex: 1,
              maxWidth: '400px'
            }}>
              <EmployeeList 
                employees={employees} 
                onDeleteEmployee={handleDeleteEmployee}
                onEditEmployee={handleEditEmployee}
                onViewEmployee={handleViewEmployee}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                showSearchOnly={true}
              />
            </div>

            {/* Add Employee Button */}
            {hasAddPermission('employee') && (
              <div className="employees-actions">
                <button
                  onClick={() => setIsFormOpen(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#26C3C0',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'Urbanist, Arial, sans-serif',
                    transition: 'background-color 0.2s ease',
                    boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#22B0AD';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#26C3C0';
                  }}
                >
                  <AddIcon style={{ fontSize: '20px' }} />
                  Add Employee
                </button>
              </div>
            )}
          </div>

          {/* User List Table */}
          <div className="employees-list-container">
            <EmployeeList 
              employees={employees} 
              onDeleteEmployee={handleDeleteEmployee}
              onEditEmployee={handleEditEmployee}
              onViewEmployee={handleViewEmployee}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              showSearchOnly={false}
            />
          </div>
        </div>
      ) : null}

      {/* Employee Form */}
      <EmployeeForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditData(null);
        }}
        editData={editData}
        onSubmit={(response) => {
          console.log('Employee operation successful:', response);
          // Refresh the employees list
          const fetchEmployees = async () => {
            try {
              const response = await dataAPI.getAllEmployees();
              setEmployees(response.data || []);
              setInfoMessage(response.message || response.data?.message || '');
            } catch (error) {
              console.error('Error refreshing employees:', error);
            }
          };
          fetchEmployees();
        }}
      />
    </div>
  );
};

export default Employees;
